// File Metadata:
// Filename: shipmentlogsApiSlice.js
// Version: 1.00
// Created: 2022-10-24 17:51:23
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { createSelector, createEntityAdapter } from '@reduxjs/toolkit'
import { apiSlice } from '../../app/api/apiSlice'

const shipmentlogsAdapter = createEntityAdapter({
	sortComparer: (a, b) => (a.completed === b.completed ? 0 : a.completed ? 1 : -1)
})
const initialState = shipmentlogsAdapter.getInitialState()

export const shipmentlogsApiSlice = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getShipmentlogs: builder.query({
			query: () => ({
				url: '/shipmentlogs',
				validateStatus: (response, result) => {
					return response.status === 200 && !result.isError
				}
			}),
			transformResponse: responseData => {
				const loadedShipmentlogs = responseData.map(shipmentlog => {
					shipmentlog.id = shipmentlog._id
					return shipmentlog
				})
				return shipmentlogsAdapter.setAll(initialState, loadedShipmentlogs)
			},
			providesTags: (result, error, arg) => {
				if (result?.ids) {
					return [{ type: 'Shipmentlog', id: 'LIST' }, ...result.ids.map(id => ({ type: 'Shipmentlog', id }))] 
				} else return [{ type: 'Shipmentlog', id: 'LIST' }]
			}
		}),
		addNewShipmentlog: builder.mutation({
			query: initialShipmentlog => ({
				url: '/shipmentlogs',
				method: 'POST',
				body: {
					...initialShipmentlog
				}
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Shipmentlog', id: 'LIST' }]
		}),
		updateShipmentlog: builder.mutation({
			query: initialShipmentlog => ({
				url: '/shipmentlogs',
				method: 'PATCH',
				body: {
					...initialShipmentlog
				}
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Shipmentlog', id: arg.id }]
		}),
		deleteShipmentlog: builder.mutation({
			query: ({ id }) => ({
				url: '/shipmentlogs',
				method: 'DELETE',
				body: { id }
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Shipmentlog', id: arg.id }]
		}),
		updateShipmentStatus: builder.mutation({
			query: ({ id, status, priority, note }) => ({
				url: `/shipmentlogs/${id}/status`,
				method: 'PATCH',
				body: { status, priority, note }
			}),
			// Optimistically update the cache
			async onQueryStarted({ id, status, priority, note }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					shipmentlogsApiSlice.util.updateQueryData('getShipmentlogs', 'shipmentlogsList', draft => {
						const shipment = draft.entities[id]
						if (shipment) {
							if (status) shipment.status = status
							if (priority) shipment.priority = priority
							if (note) {
								shipment.statusHistory = shipment.statusHistory || []
								shipment.statusHistory.push({
									status: status || priority,
									note,
									timestamp: new Date().toISOString()
								})
							}
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			invalidatesTags: (result, error, { id }) => [
				{ type: 'Shipmentlog', id }
			]
		}),
		addShipmentComment: builder.mutation({
			query: ({ id, text, author }) => ({
				url: `/shipmentlogs/${id}/comments`,
				method: 'POST',
				body: { text, author }
			}),
			// Optimistically update the cache
			async onQueryStarted({ id, text, author }, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					dispatch(
						shipmentlogsApiSlice.util.updateQueryData('getShipmentlogs', 'shipmentlogsList', draft => {
							const shipment = draft.entities[id]
							if (shipment) {
								shipment.comments = shipment.comments || []
								shipment.comments.push({
									text,
									author,
									timestamp: new Date().toISOString()
								})
							}
						})
					)
				} catch {}
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Shipmentlog', id: arg.id }
			]
		}),
		addShipmentImage: builder.mutation({
			query: ({ id, imageData }) => ({
				url: `/shipmentlogs/${id}/images`,
				method: 'POST',
				body: imageData
			}),
			// Optimistically update the cache
			async onQueryStarted({ id, imageData }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					shipmentlogsApiSlice.util.updateQueryData('getShipmentlogs', 'shipmentlogsList', draft => {
						const shipment = draft.entities[id]
						if (shipment) {
							shipment.img = shipment.img || []
							shipment.img.push(imageData)
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			}
		}),
		deleteShipmentImage: builder.mutation({
			query: ({ id, fileId }) => ({
				url: `/shipmentlogs/${id}/images/${fileId}`,
				method: 'DELETE'
			}),
			// Optimistically update the cache
			async onQueryStarted({ id, fileId }, { dispatch, queryFulfilled }) {
				const patchResult = dispatch(
					shipmentlogsApiSlice.util.updateQueryData('getShipmentlogs', 'shipmentlogsList', draft => {
						const shipment = draft.entities[id]
						if (shipment?.img) {
							shipment.img = shipment.img.filter(img => img.fileId !== fileId)
						}
					})
				)
				try {
					await queryFulfilled
				} catch {
					patchResult.undo()
				}
			},
			// Invalidate the specific shipment in the cache
			invalidatesTags: (result, error, { id }) => [
				{ type: 'Shipmentlog', id }
			]
		})
	})
})

export const {
	useGetShipmentlogsQuery,
	useAddNewShipmentlogMutation,
	useUpdateShipmentlogMutation,
	useDeleteShipmentlogMutation,
	useUpdateShipmentStatusMutation,
	useAddShipmentCommentMutation,
	useAddShipmentImageMutation,
	useDeleteShipmentImageMutation
} = shipmentlogsApiSlice

// returns the query result object
export const selectShipmentlogsResult = shipmentlogsApiSlice.endpoints.getShipmentlogs.select()

// creates memoized selector
const selectShipmentlogsData = createSelector(
	selectShipmentlogsResult,
	shipmentlogsResult => shipmentlogsResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
	selectAll: selectAllShipmentlogs,
	selectById: selectShipmentlogById,
	selectIds: selectShipmentlogIds
	// Pass in a selector that returns the shipmentlogs slice of state
} = shipmentlogsAdapter.getSelectors(state => selectShipmentlogsData(state) ?? initialState)
